import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { CurrencyPipe } from '@frontend/vanilla/core';
import { TranslateModule } from '@ngx-translate/core';
import { AuthDetailsStore } from '@payments/common-utils';
import { CardSubmitRequest, CardSubmitUseCase, EventType } from '@payments/ngpgcard-utils';
import { Subscription } from 'rxjs';

import { AppConstants, Config, MOBILE_APP_CHANNELS } from '../../../config/appConstants';
import { SessionInfo } from '../../../model/common.model';
import { DepositInfo } from '../../../model/depositInfo.model';
import { QDInstrument } from '../../../model/qdInstrument.model';
import { ApplePayDirectService } from '../../../service/apple-pay-direct-service.service';
import { BehaviorDataService } from '../../../service/behaviorData.service';
import { BetslipQdMessageService } from '../../../service/betslip-qd-message.service';
import { BonusService } from '../../../service/bonus.service';
import { CardIframeService } from '../../../service/card-iframe-service.service';
import { DataService } from '../../../service/data.service';
import { DepositService } from '../../../service/deposit.service';
import { GeocomplyService } from '../../../service/geocomply.service';
import { GooglePayService } from '../../../service/google-pay.service';
import { GtmService } from '../../../service/gtm.service';
import { LoaderService } from '../../../service/loader.service';
import { OptionListService } from '../../../service/optionList.service';
import { StakeAmountService } from '../../../service/stakeAmount.service';
import { StoreService } from '../../../service/store.service';
import { UserDataService } from '../../../service/userDataService';
import { WorldPayFetchSessionIdService } from '../../../service/worldPayFetchSessionId.service';
import { GPAY_CONSTANTS } from '../../../shared/error.constants';
import { AdditionalMessagesComponent } from '../common/additional-messages/additional-messages.component';
import { DepositCurfewMessageComponent } from '../common/additional-messages/deposit-curfew-message/deposit-curfew-message.component';
import { RedirectMessageComponent } from '../common/additional-messages/redirect-message/redirect-message.component';
import { DepositFailureComponent } from '../common/deposit-failure/deposit-failure.component';
import { OptionsListComponent } from '../common/options-list/options-list.component';
import { ReversalInputComponent } from './reversal-input/reversal-input.component';

@Component({
    selector: 'app-gvc-betslip',
    templateUrl: 'gvc-betslip.component.html',
    providers: [GeocomplyService],
    imports: [
        DepositFailureComponent,
        AdditionalMessagesComponent,
        FormsModule,
        ReactiveFormsModule,
        OptionsListComponent,
        RedirectMessageComponent,
        ReversalInputComponent,
        CurrencyPipe,
        TranslateModule,
        DepositCurfewMessageComponent,
    ],
})
export class GVCBetslipComponent implements OnInit, AfterViewChecked, OnDestroy {
    private store = inject(StoreService);
    sessionInfo: SessionInfo;
    depositForm: FormGroup;
    bonusInputFormGroup: FormGroup;
    kycPLverification: boolean;
    inputType: string;
    ccFamily = AppConstants.CCFAMILY;
    depositInfo: DepositInfo;
    selectedOption: QDInstrument;
    worldPaySessionIdStatus: boolean;
    // currency: string;
    activeDepositCurfew: boolean;
    subscriptions$: Subscription[] = [];
    bonusTnc = true;
    isVisible = false;
    showAdaInteractionMessage = this.store.getAdaInteractionMessage;
    fundsNeeded: any;

    constructor(
        public depositService: DepositService,
        private userDataService: UserDataService,
        private dataService: DataService,
        private behaviorData: BehaviorDataService,
        public optionListService: OptionListService,
        private changeDetector: ChangeDetectorRef,
        private wpSessionService: WorldPayFetchSessionIdService,
        private GTMService: GtmService,
        private formBuilder: FormBuilder,
        private bonusService: BonusService,
        private geoComply: GeocomplyService,
        private stakeAmountService: StakeAmountService,
        private applePayDirectService: ApplePayDirectService,
        private gpayService: GooglePayService,
        private cardIframeService: CardIframeService,
        private betslipqdMessageService: BetslipQdMessageService,
        public loaderService: LoaderService,
        private authDetailsStore: AuthDetailsStore,
    ) {
        effect(() => {
            const inputMessage = this.betslipqdMessageService.input();
            if (inputMessage?.eventName) {
                this.handleParentMessage({ data: inputMessage } as MessageEvent);
            }
            const continuDepoist = this.store.getcontinueDeposit();
            if (continuDepoist()) {
                this.submitDeposit();
            }
        });
    }

    ngOnInit() {
        this.sessionInfo = this.dataService.sessionInfo;
        this.GTMService.raiseOnLoadGTMEvent();
        this.buildForm();
        this.depositService.showDepositInput.set(true);
        this.initialize();
        this.getBehaviorData();
    }
    updatePaymentMethod(payName: string) {
        this.inputType = payName;
    }

    initialize() {
        this.setDepositOptionInfo();
    }

    get instrumentFormGroup(): FormGroup {
        return this.depositForm.controls['instrumentFormGroup'] as FormGroup;
    }

    setDepositOptionInfo() {
        this.selectedOption = this.optionListService?.selectedOption;
        this.depositService.depositInfo.amountInfo.nativeCurrency =
            this.selectedOption?.currencyInfo?.defaultCurr || this.selectedOption?.currencyInfo?.currencyList[0];
        if (this.userDataService?.userData?.configParams) {
            const configParams = this.userDataService?.userData?.configParams;
            this.depositService.depositInfo.isBelgiumRgDecreeEnabled = configParams?.belgium_rg_decree_enabled === Config.YES;
            if (this.depositService?.depositInfo?.isBelgiumRgDecreeEnabled) {
                this.depositService.depositInfo.increaseLimitUrl = configParams?.deposit_Limits_Url;
            }
        }

        const threeds2Enabled = this.selectedOption?.threeds2Enabled;
        if (threeds2Enabled && this.userDataService?.userData?.configParams?.worldPayTokenForDDC) this.getSessionIdForWP();
        this.geoComplyInit();
        this.isVisible = true;
    }
    geoComplyInit() {
        const geoComplyLabel: string | undefined = this.userDataService?.userData?.configParams?.GEOCOMPLY_LABEL_NAME;
        const configParams = this.userDataService?.userData?.configParams;
        const channelId = this.userDataService?.userData?.userInfo?.labelInfo?.channelId || '';
        if (configParams?.merchant_hostname && geoComplyLabel && !MOBILE_APP_CHANNELS.includes(channelId)) {
            this.geoComply.loadGeoComplyScript(this.userDataService?.userData);
        }
    }
    getActiveDepositCurfew(data: boolean) {
        this.activeDepositCurfew = data;
    }

    getSessionIdForWP() {
        const { worldpayTimerDuration = 5 } = this.userDataService?.userData?.configParams || {};
        const timerDuration = worldpayTimerDuration * 1000;
        this.fetchWorldPaySessionId();

        setTimeout(() => {
            if (!this.worldPaySessionIdStatus) {
                this.setWorldPaySessionId('', {
                    reason: 'wp_timeout',
                    info: 'not_received_sessionId_within_duration',
                });
            }
        }, timerDuration); //worldpayTimerDuration is a config driven , default is 5000
    }
    fetchWorldPaySessionId() {
        this.wpSessionService.fetchWPSessionId(
            this.userDataService?.userData?.configParams?.baseWorldPayURLForDDC,
            this.userDataService?.userData?.configParams?.worldPayTokenForDDC,
            this.userDataService?.userData?.configParams?.worldPayURLForDDC,
            this.selectedOption?.startDigits,
        );
    }

    setWorldPaySessionId(worldPaySessionId: any, data: any) {
        this.depositService.depositInfo.worldPaySessionId = worldPaySessionId;
        if (data?.reason == 'worldPayResponse') this.depositService.depositInfo.paymentAccountInfo.worldPayResponse = JSON.stringify(data.info);
        else if (data?.reason == 'wp_timeout') this.depositService.depositInfo.paymentAccountInfo.wpTimeout = data.info;
        this.worldPaySessionIdStatus = true;
    }

    handleParentMessageEvent(eName = 'FundsAdded', status = false) {
        const data: { eventName: string; eventData: { [key: string]: boolean } } = {
            eventName: eName,
            eventData: {},
        };
        if (eName === 'FundsAdded') {
            data.eventData['success'] = status;
        } else {
            data.eventData['available'] = status;
        }
        this.depositService.postMessageToParent(data, '*');
    }
    submitDeposit() {
        // checking this as deposit button is enabled even form is not valid.
        if (!this.depositForm.valid) {
            this.depositForm.markAllAsTouched();
            this.handleParentMessageEvent();
            return false;
        }
        this.depositService.showDepositFailure.set(false);
        this.depositService.resizeCashier();
        let proceedToSubmit = true;
        const threeds2Enabled = this.optionListService?.selectedOption?.threeds2Enabled;
        if (this.userDataService?.userData?.configParams?.worldPayTokenForDDC) {
            proceedToSubmit = false;
        }

        if (this.activeDepositCurfew) {
            this.behaviorData.changeCVV2('');
            this.handleParentMessageEvent();
            return false;
        }
        if (threeds2Enabled) {
            this.fetchWorldPaySessionId();
        }
        this.geoComplyInitialize();
        if (this.selectedOption?.paymentMethodInfo?.paymentMethod == 'INTERCHECKS_ACH') {
            const { paymentAccountId: accountId, accountHash, routingNumber } = this.selectedOption.paymentAccountInfo;
            this.depositService.depositInfo.paymentAccountInfo.cardHash = accountHash;
            this.depositService.depositInfo.paymentAccountInfo.routingNumber = routingNumber;
            this.depositService.depositInfo.paymentAccountInfo.accountId = accountId;
        }
        if (!threeds2Enabled || proceedToSubmit) {
            if (this.optionListService?.selectedOption?.paymentMethodInfo?.isNonPCIOption === 'true') {
                this.submitCCDeposit();
            } else {
                if (['APPLEPAYDIRECT', 'APPLEPAY'].includes(this.depositService.depositInfo.paymentMethod?.toUpperCase())) {
                    this.applePayDepositSubmit();
                } else if (GPAY_CONSTANTS.GOOGLEPAY == this.depositService.depositInfo.paymentMethod?.toUpperCase()) {
                    const feid = this.userDataService.userData?.userInfo?.labelInfo?.feId;
                    this.gpayService.loadPaymentMethods(feid);
                    this.gpayService.loadGpayPaymentResponse();
                } else {
                    this.depositService.submitDeposit();
                }
            }
        } else {
            this.depositService.depositInfo.worldPaySessionId = this.dataService?.worldPaySessionId;
            this.submitCCDeposit();
        }
        const bonusCode = this.depositInfo?.amountInfo?.bonusCode;
        this.bonusService.isBonusError = false;
        if (bonusCode && this.bonusService?.validatedBonusCode !== bonusCode) {
            this.bonusService.isBonusValidationOnSubmit = true;
            this.bonusService.handleBonusAmount(this.depositInfo);
            return false;
        } else if (this.bonusService.bonusError === '808') {
            this.bonusService.showAlertPopup();
            return false;
        }
        return false;
    }
    geoComplyInitialize(): void {
        if (this.userDataService?.userData?.configParams?.GEOCOMPLY_LABEL_NAME) {
            this.geoComply.getGeoComplyInitializeInput(this.userDataService?.userData);
        }
    }

    private submitCCDeposit() {
        // NGPG  Submit  Call
        // if (this.userDataService?.userData?.configParams?.qd_ngpg_iframe_enabled === 'Y') {
        //CardSubmitRequest
        const request: CardSubmitRequest = {
            submitUrl: this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountUpdateUrl,
            jwtToken: this.optionListService?.selectedOption?.paymentAccountInfo?.cardToken ?? '',
            usecase: CardSubmitUseCase.update_cvv,
        };
        this.cardIframeService._eventBridge()?.off(EventType.card_submit_response);
        this.cardIframeService._eventBridge()?.sendMessageToIframe({ eventName: EventType.card_submit, payload: request });
        this.cardIframeService._eventBridge()?.receiveEvent(EventType.card_submit_response, this.handleIframeResponse.bind(this));
        // } else {
        //     this.ccDepositService.submitCCDeposit(
        //         this.depositService?.depositInfo?.paymentAccountInfo?.cvv2,
        //         this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountUpdateUrl,
        //         this.depositService.handleResponse,
        //         this.optionListService?.selectedOption?.paymentAccountInfo?.cardToken,
        //     );
        // }
    }
    private handleIframeResponse(data: any) {
        if (!data?.errorcode) {
            this.depositService.submitDeposit();
        }
    }

    getBehaviorData() {
        this.kycPLverification = ['SUSPENDED_GRACE_DAYS_OVER', 'SUSPENDED_DEPOSIT_AMOUNT_EXCEEDED'].includes(
            this.userDataService?.userData?.userInfo?.ezecashCategoryComment,
        );

        this.subscriptions$.push(
            this.behaviorData?.isBetSuspended.subscribe((data) => {
                this.handleParentMessageEvent('DepositAvailable', !data);
            }),
        );
        this.subscriptions$.push(
            this.behaviorData?.changeAmountRequiredToMeetStake.subscribe((data) => {
                this.fundsNeeded = data;
            }),
        );

        this.subscriptions$.push(
            this.behaviorData?.getEnteredAmount.subscribe(() => {
                this.formValidationListner();
            }),
        );

        this.subscriptions$.push(
            this.behaviorData?.getCVV2.subscribe(() => {
                this.formValidationListner();
            }),
        );
        // this.subscriptions$.push(
        //     this.behaviorData?.getVoucherPin.subscribe(() => {
        //         this.formValidationListner();
        //     }),
        // );
        this.subscriptions$.push(
            this.behaviorData?.getbonusTnC.subscribe((data) => {
                this.bonusTnc = data;
                this.formValidationListner();
            }),
        );

        //todo check for gamerscard
    }
    updateVoucher() {
        this.formValidationListner();
    }

    formValidationListner() {
        // Event listener to post message to parent on form valid or invalid
        setTimeout(() => {
            this.handleParentMessageEvent('DepositAvailable', this.depositForm.valid && this.bonusTnc);
        }, 100);
    }

    handleParentMessage(event: MessageEvent) {
        const message = event;
        const eventName = message?.data?.eventName;
        if (['CallSubmitGvcBetslipMethod', 'Deposit'].includes(eventName)) {
            this.submitDeposit();
        }
        this.stakeAmountService.addEventListener(event);
    }

    buildForm() {
        this.depositForm = this.formBuilder.group({
            instrumentFormGroup: new FormGroup({}),
            ccInputFormGroup: new FormGroup({}),
        });
    }
    ngAfterViewChecked() {
        this.changeDetector.detectChanges();
    }

    ngOnDestroy() {
        this.subscriptions$.map((subscription) => subscription.unsubscribe());
        this.authDetailsStore.initialize();
        this.betslipqdMessageService.destroy();
    }

    applePayDepositSubmit() {
        const paymentMethod = this.depositService.depositInfo.paymentMethod?.toUpperCase();
        const applePayData = this.applePayDirectService.setApplePayData(paymentMethod);

        let creditCardBanEnabled = 'N';
        const country = this.userDataService?.userData?.userInfo?.addressInfo?.country;
        if (paymentMethod == 'APPLEPAYDIRECT') {
            const { isESIntensivePlayer, paymentAccountUpdateUrl, cardToken } = this.optionListService?.selectedOption?.paymentAccountInfo || {};
            creditCardBanEnabled =
                isESIntensivePlayer == 'Y' || this.userDataService?.userData?.configParams?.creditCardBanEnabled == 'Y' ? 'Y' : 'N';
            this.applePayDirectService.processApplePayDeposit(applePayData, creditCardBanEnabled, country, paymentAccountUpdateUrl, cardToken);
        }
    }
}
